import moment from "moment";

import { isValidDate } from "../../../utils/date-format.utils";
import { TWorkOrderResults } from "../../../redux/reducers/workOrdersReducer";
import { WORK_ORDER_TEMPLATE } from "../../../types/templates/work-order/work-order-template.enum";

export const getWorkOrderFormDefaultValues = (workOrder: TWorkOrderResults) => {
  switch (workOrder.resourcetype) {
    case WORK_ORDER_TEMPLATE.DEALER_ADVERTISING:
      return selectDealerAdvertisingWorkOrderTemplateData(workOrder);

    case WORK_ORDER_TEMPLATE.WEB:
      return selectWebBannerWorkOrderTemplateData(workOrder);

    case WORK_ORDER_TEMPLATE.INSTORE_PRINT:
      return selectInStorePrintWorkOrderTemplateData(workOrder);

    case WORK_ORDER_TEMPLATE.INSTORE_DIGITAL:
      return selectInStoreDigitalWorkOrderTemplateData(workOrder);

    case WORK_ORDER_TEMPLATE.PAID_MEDIA:
      return selectPaidMediaWorkOrderTemplateData(workOrder);

    case WORK_ORDER_TEMPLATE.EDM:
      return selectEdmWorkOrderTemplateData(workOrder);

    case WORK_ORDER_TEMPLATE.SIMPLE:
    default:
      return selectSimpleWorkOrderTemplateData(workOrder);
  }
};

const getUserDetails = (workOrder: TWorkOrderResults) => {
  return workOrder.contentCreators.map((creator) => creator.userDetails) ?? [];
};

export const selectSimpleWorkOrderTemplateData = (workOrder: TWorkOrderResults) => ({
  additionalCreativeDetails: workOrder.additionalCreativeDetails ?? "",
  areVendorAssetsProvided: workOrder.areAdditionalAssetsProvided ?? false,
  assetType: workOrder.assetType ?? "",
  contentCreators: workOrder.contentCreators ?? [],
  contentCreatorsSelection: getUserDetails(workOrder),
  dueAt: isValidDate(workOrder.dueAt ?? "") ? moment(workOrder.dueAt) : null,
  endAt: isValidDate(workOrder.endAt ?? "") ? moment(workOrder.endAt) : null,
  fileFormat: workOrder.fileFormat ?? "",
  fileFormatDescription: workOrder.fileFormatDescription ?? "",
  isBuyGet: workOrder.isBuyGet ?? false,
  isCreativeReviewRequired: workOrder.isCreativeReviewRequired ?? false,
  legalDetails: workOrder.legalDetails ?? "",
  mandatoryCreativeDetails: workOrder.mandatoryCreativeDetails ?? "",
  primaryMessage: workOrder.primaryMessage ?? "",
  secondaryMessage: workOrder.secondaryMessage ?? "",
  size: workOrder.size ?? "",
  startAt: isValidDate(workOrder.startAt ?? "") ? moment(workOrder.startAt) : null,
  status: workOrder.status ?? "draft",
  vendorAssetsLocation: workOrder.vendorAssetsLocation ?? "",
});

export const selectWebBannerWorkOrderTemplateData = (workOrder: TWorkOrderResults) => {
  return {
    contentCreators: workOrder.contentCreators ?? [],
    contentCreatorsSelection: getUserDetails(workOrder),
    cta: workOrder.cta ?? "",
    dueAt: isValidDate(workOrder.dueAt ?? "") ? moment(workOrder.dueAt) : null,
    endAt: isValidDate(workOrder.endAt ?? "") ? moment(workOrder.endAt) : null,
    fileFormat: workOrder.fileFormat ?? "",
    fileFormatDescription: workOrder.fileFormatDescription ?? "",
    isBuyGet: workOrder.isBuyGet ?? false,
    isCreativeReviewRequired: workOrder.isCreativeReviewRequired ?? false,
    isSegmented: workOrder.isSegmented ?? false,
    legal: workOrder.legal ?? "",
    notesForCreative: workOrder.notesForCreative ?? "",
    notesForEcomm: workOrder.notesForEcomm ?? "",
    positions: workOrder.positions ?? [],
    primaryMessage: workOrder.primaryMessage ?? "",
    secondaryMessage: workOrder.secondaryMessage ?? "",
    segments: workOrder.segments ?? "",
    sitePlacement: workOrder.sitePlacement ?? "",
    startAt: isValidDate(workOrder.startAt ?? "") ? moment(workOrder.startAt) : null,
    status: workOrder.status ?? "draft",
  };
};

export const selectDealerAdvertisingWorkOrderTemplateData = (workOrder: TWorkOrderResults) => ({
  additionalAssetsLocation: workOrder.additionalAssetsLocation ?? "",
  areAdditionalAssetsProvided: workOrder.areAdditionalAssetsProvided ?? false,
  assetDescription: workOrder.assetDescription ?? "",
  assetRequest: workOrder.assetRequest ?? "",
  assetType: workOrder.assetType ?? "",
  colour: workOrder.colour ?? "",
  contentCreators: workOrder.contentCreators ?? [],
  contentCreatorsSelection: getUserDetails(workOrder),
  copyDirection: workOrder.copyDirection ?? "",
  dueAt: isValidDate(workOrder.dueAt) ? moment(workOrder.dueAt) : null,
  endAt: isValidDate(workOrder.endAt ?? "") ? moment(workOrder.endAt) : null,
  fileFormat: workOrder.fileFormat ?? "",
  fileFormatDescription: workOrder.fileFormatDescription ?? "",
  isBuyGet: workOrder.isBuyGet ?? false,
  isCreativeReviewRequired: workOrder.isCreativeReviewRequired ?? false,
  objectiveOrPurpose: workOrder.objectiveOrPurpose ?? "",
  size: workOrder.size ?? "",
  startAt: isValidDate(workOrder.startAt ?? "") ? moment(workOrder.startAt) : null,
  status: workOrder.status ?? "draft",
});

export const selectInStorePrintWorkOrderTemplateData = (workOrder: TWorkOrderResults) => ({
  assetDescription: workOrder.assetDescription ?? "",
  assetDimension: workOrder.assetDimension ?? "",
  assetType: workOrder.assetType ?? "",
  contentCreators: workOrder.contentCreators ?? [],
  contentCreatorsSelection: getUserDetails(workOrder),
  contentDirection: workOrder.contentDirection ?? "",
  dueAt: isValidDate(workOrder.dueAt ?? "") ? moment(workOrder.dueAt) : null,
  endAt: isValidDate(workOrder.endAt ?? "") ? moment(workOrder.endAt) : null,
  englishNumberOfStores: workOrder.englishNumberOfStores ?? "",
  englishQtyPerStore: workOrder.englishQtyPerStore ?? "",
  englishRequired: workOrder.englishRequired ?? false,
  fileFormat: workOrder.fileFormat ?? "",
  fileFormatDescription: workOrder.fileFormatDescription ?? "",
  frenchNumberOfStores: workOrder.frenchNumberOfStores ?? "",
  frenchQtyPerStore: workOrder.frenchQtyPerStore ?? "",
  frenchRequired: workOrder.frenchRequired ?? false,
  isBuyGet: workOrder.isBuyGet ?? false,
  isCreativeReviewRequired: workOrder.isCreativeReviewRequired ?? false,
  offerDetails: workOrder.offerDetails ?? "",
  popStoreTypes: workOrder.popStoreTypes ?? "",
  portalOrPrint: workOrder.portalOrPrint ?? "",
  startAt: isValidDate(workOrder.startAt ?? "") ? moment(workOrder.startAt) : null,
  status: workOrder.status ?? "draft",
  warehouseEta: isValidDate(workOrder.warehouseEta ?? "") ? moment(workOrder.warehouseEta) : null,
});

export const selectInStoreDigitalWorkOrderTemplateData = (workOrder: TWorkOrderResults) => ({
  assetDescription: workOrder.assetDescription ?? "",
  assetDimension: workOrder.assetDimension ?? "",
  assetType: workOrder.assetType ?? "",
  contentCreators: workOrder.contentCreators ?? [],
  contentCreatorsSelection: getUserDetails(workOrder),
  contentDirection: workOrder.contentDirection ?? "",
  dueAt: isValidDate(workOrder.dueAt) ? moment(workOrder.dueAt) : null,
  endAt: isValidDate(workOrder.endAt ?? "") ? moment(workOrder.endAt) : null,
  englishNumberOfScreens: workOrder.englishNumberOfScreens ?? "",
  englishRequired: workOrder.englishRequired ?? false,
  fileFormat: workOrder.fileFormat ?? "",
  fileFormatDescription: workOrder.fileFormatDescription ?? "",
  frenchNumberOfScreens: workOrder.frenchNumberOfScreens ?? "",
  frenchRequired: workOrder.frenchRequired ?? false,
  isBuyGet: workOrder.isBuyGet ?? false,
  isCreativeReviewRequired: workOrder.isCreativeReviewRequired ?? false,
  offerDetails: workOrder.offerDetails ?? "",
  startAt: isValidDate(workOrder.startAt ?? "") ? moment(workOrder.startAt) : null,
  status: workOrder.status ?? "draft",
});

export const selectPaidMediaWorkOrderTemplateData = (workOrder: TWorkOrderResults) => ({
  adFormat: workOrder.adFormat ?? "",
  adMedium: workOrder.adMedium ?? "",
  adSpecifications: workOrder.adSpecifications ?? "",
  adType: workOrder.adType ?? "",
  areVendorAssetsProvided: workOrder.areVendorAssetsProvided ?? false,
  assetLanguages: workOrder.assetLanguages ?? [],
  brandsIncluded: workOrder.brandsIncluded ?? "",
  campaignTag: workOrder.campaignTag ?? "",
  communicationsApproach: workOrder.communicationsApproach ?? "",
  contentCreators: workOrder.contentCreators ?? [],
  contentCreatorsSelection: getUserDetails(workOrder),
  contentName: workOrder.contentName ?? "",
  copyDirection: workOrder.copyDirection ?? "",
  dueAt: isValidDate(workOrder.dueAt ?? "") ? moment(workOrder.dueAt) : null,
  endAt: isValidDate(workOrder.endAt ?? "") ? moment(workOrder.endAt) : null,
  englishAssetUnitLinks: workOrder.englishAssetUnitLinks ?? [],
  frenchAssetUnitLinks: workOrder.frenchAssetUnitLinks ?? [],
  funnel: workOrder.funnel ?? "",
  hasVendorCommitment: workOrder.hasVendorCommitment ?? false,
  isBuyGet: workOrder.isBuyGet ?? false,
  isCreativeReviewRequired: workOrder.isCreativeReviewRequired ?? false,
  isRegional: workOrder.isRegional ?? false,
  isSkuListRequired: workOrder.isSkuListRequired ?? false,
  isVendorApprovalRequired: workOrder.isVendorApprovalRequired ?? false,
  phdBudget: workOrder.phdBudget ?? "",
  projectOverview: workOrder.projectOverview ?? "",
  regions: workOrder.regions ?? [],
  reportingMasterCampaignName: workOrder.reportingMasterCampaignName ?? "",
  skuListUploadLocation: workOrder.skuListUploadLocation ?? "",
  startAt: isValidDate(workOrder.startAt ?? "") ? moment(workOrder.startAt) : null,
  status: workOrder.status ?? "draft",
  theme: workOrder.theme ?? "",
  traffickingDueAt: isValidDate(workOrder.traffickingDueAt ?? "")
    ? moment(workOrder.traffickingDueAt)
    : null,
  uploadLocation: workOrder.uploadLocation ?? "",
  vendorAssetsLocation: workOrder.vendorAssetsLocation ?? "",
});

export const selectEdmWorkOrderTemplateData = (workOrder: TWorkOrderResults) => ({
  contentCreators: workOrder.contentCreators ?? [],
  contentCreatorsSelection: getUserDetails(workOrder),
  description: workOrder.description ?? "",
  deploymentAt: isValidDate(workOrder.deploymentAt ?? "") ? moment(workOrder.deploymentAt) : null,
  dueAt: isValidDate(workOrder.dueAt ?? "") ? moment(workOrder.dueAt) : null,
  endAt: isValidDate(workOrder.endAt ?? "") ? moment(workOrder.endAt) : null,
  fromPricing: workOrder.fromPricing ?? "",
  isBuyGet: workOrder.isBuyGet ?? false,
  isCreativeReviewRequired: workOrder.isCreativeReviewRequired ?? false,
  positionDescription: workOrder.positionDescription ?? "",
  preHeaderDirection: workOrder.preHeaderDirection ?? "",
  startAt: isValidDate(workOrder.startAt ?? "") ? moment(workOrder.startAt) : null,
  status: workOrder.status ?? "draft",
  subjectLineDirection: workOrder.subjectLineDirection ?? "",
  utmCampaign: workOrder.utmCampaign ?? "",
  utmContent: workOrder.utmContent ?? "",
  utmMedia: workOrder.utmMedia ?? "",
  utmSource: workOrder.utmSource ?? "",
  utmTerm: workOrder.utmTerm ?? "",
});
